/**
 * Mobile Team Header Component
 *
 * Shows Pharma Medical Team details in a collapsible card format for mobile view.
 * Displays logo, badges, selected product, and expandable profile button.
 */

import { Dna } from 'lucide-react-native';

import { primum_default_avatar } from '@/assets/images';
import { ProductSupportBadge } from '@/components/MedicalOnDemand/ProductSupportBadge';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Button, ButtonText } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Link, LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import {
  getProductData,
  PharmaProductSupportTeam,
  Product,
  ProductId,
  TREATMENT_SUPPORT_LABEL,
} from '@/constants/medicalOnDemand';
import { useExpertAvatar } from '@/hooks/useExpertAvatar';
import { useModTeamName } from '@/hooks/useModTeamName';

type ProductSupportMobileHeaderProps = {
  onOpenGuidelines: () => void;
  onOpenAbout: () => void;
  pharmaProductSupportTeam: PharmaProductSupportTeam;
  selectedProductIds?: ProductId[];
};

export function ProductSupportMobileHeader({
  onOpenGuidelines,
  onOpenAbout,
  pharmaProductSupportTeam,
  selectedProductIds,
}: ProductSupportMobileHeaderProps) {
  const selectedProducts = (selectedProductIds ?? [])
    .map((id) => getProductData(id))
    .filter((p): p is Product => !!p);

  const expertAvatarUrl = useExpertAvatar(pharmaProductSupportTeam.expertId);

  const teamName = useModTeamName(pharmaProductSupportTeam.name);

  return (
    <>
      <VStack className="bg-background-0 px-5 pb-6">
        <HStack className="items-start gap-4">
          <Avatar size="md">
            <AvatarImage
              source={expertAvatarUrl ? { uri: expertAvatarUrl } : primum_default_avatar}
              alt={teamName + ' avatar'}
            />
          </Avatar>
          <VStack className="flex-1 gap-2">
            <Heading size="md" className="text-typography-900">
              {pharmaProductSupportTeam.specialty}
            </Heading>
            <HStack className="flex-wrap items-center gap-3">
              <ProductSupportBadge size="sm" />
              <Text className="text-xs text-typography-800">{teamName}</Text>
            </HStack>
          </VStack>
        </HStack>

        <Divider className="my-3" />

        {selectedProducts.length === 0 && (
          <VStack className="mb-3">
            <HStack className="items-center gap-2 py-2">
              <Icon as={Dna} size="sm" />
              <Text className="text-sm text-typography-800">
                Treatments for: <Text className="font-semibold text-sm">MM</Text>
              </Text>
            </HStack>
          </VStack>
        )}

        {selectedProducts.length === 1 && (
          <VStack className="mb-3">
            <HStack className="items-center gap-2 py-2">
              <Text className="text-sm text-typography-800">
                {selectedProducts[0].brandName}{' '}
                <Text className="text-sm text-typography-800">
                  ({selectedProducts[0].genericName})
                </Text>
              </Text>
            </HStack>
          </VStack>
        )}

        {selectedProducts.length > 1 && (
          <VStack className="mb-3">
            {selectedProducts.map((product) => (
              <HStack key={product.id} className="items-center gap-2 py-2">
                <Text className="text-sm text-typography-800">
                  {product.brandName}{' '}
                  <Text className="text-sm text-typography-800">({product.genericName})</Text>
                </Text>
              </HStack>
            ))}
          </VStack>
        )}

        <Button
          variant="outline"
          action="secondary"
          size="sm"
          onPress={onOpenAbout}
          className="w-full">
          <ButtonText>View profile</ButtonText>
        </Button>
      </VStack>

      <VStack className="my-2 gap-2 rounded-md bg-background-info px-5 py-5">
        <Text className="font-medium text-sm text-typography-900">Ask {teamName}</Text>
        <Text className="text-xs text-typography-900">{pharmaProductSupportTeam.guidance}</Text>
        <Link onPress={onOpenGuidelines}>
          <LinkText className="text-xs">{TREATMENT_SUPPORT_LABEL} Guidelines</LinkText>
        </Link>
      </VStack>
    </>
  );
}
